import React, { useRef } from "react"
import styled from "styled-components"
import colors from "~constants/colors"

interface ObjectEditorProps {
  data: object
  onChange: (o: object) => void
  type: "number" | "text"
  disabled: boolean
  aiFilledFields?: Set<string>
  aiSuggestsReject?: boolean
  aiSuggestsReview?: boolean
  columns?: number
  hideEmpty?: boolean
  labels?: Record<string, string>
  stackLabel?: boolean
}

const ObjectEditor = ({
  data,
  type,
  onChange,
  disabled,
  aiFilledFields,
  aiSuggestsReject = false,
  aiSuggestsReview = false,
  columns = 2,
  hideEmpty = false,
  labels,
  stackLabel = false,
}: ObjectEditorProps) => {
  // Uncontrolled inputs: `defaultValue` seeds each field once, then the DOM
  // owns the value while the user types. We do NOT re-render on keystrokes, so
  // a background refetch can never interleave and revert an in-progress edit.
  // A ref accumulates the current values and `onChange` forwards them to the
  // parent, which keeps the form store in sync without re-rendering.
  //
  // To reseed (different user, broker switch, AI fill), the parent remounts
  // this component via a `key`, which re-applies `defaultValue` from new data.
  const valuesRef = useRef<Record<string, any>>({ ...data })

  const handleOnChange = (
    key: string,
    e: React.ChangeEvent<HTMLInputElement>,
  ) => {
    valuesRef.current[key] = e.target.value
    onChange({ ...valuesRef.current })
  }

  const keys = Object.keys(data).filter(key => {
    if (!hideEmpty) return true
    const val = data[key]
    return val !== "" && val !== 0 && val !== null && val !== undefined
  })

  return (
    <Wrapper $columns={columns}>
      {keys.map(key => {
        const isAi = aiFilledFields && aiFilledFields.has(key)
        const aiClass = !isAi
          ? ""
          : aiSuggestsReject
            ? "ai-filled-field ai-filled-field--reject"
            : aiSuggestsReview
              ? "ai-filled-field ai-filled-field--review"
              : "ai-filled-field"
        return (
          <Row key={key} className={aiClass}>
            {stackLabel && labels?.[key] ? (
              <LabelStack>
                <span>{labels[key].replace(/\s*\(.*\)$/, "")}</span>
                <LabelCode>{labels[key].match(/\(([^)]+)\)/)?.[1]}</LabelCode>
              </LabelStack>
            ) : (
              <Label>{labels?.[key] ?? key}</Label>
            )}
            <DataInput
              type={type}
              step={"any"}
              defaultValue={data[key] ?? ""}
              name={key}
              onChange={e => handleOnChange(key, e)}
              // Prevent the mouse wheel from silently changing the value while
              // the field is focused (a number-input footgun): scrolling toward
              // the Save button would otherwise decrement the share count.
              onWheel={e => e.currentTarget.blur()}
              min={0}
              disabled={disabled}
            />
          </Row>
        )
      })}
    </Wrapper>
  )
}

const Wrapper = styled.div<{ $columns: number }>`
  display: grid;
  grid-template-columns: repeat(${p => p.$columns}, 1fr);
  gap: 2px 12px;
`

const Row = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 4px 8px;
  border-radius: 4px;
  transition: background 0.15s;

  &:hover {
    background: ${colors.gray5};
  }

  &.ai-filled-field {
    background: #f0fdf4;
    border-left: 3px solid #4caf50;

    input {
      border-color: #4caf50;
      color: #14532d;
      font-weight: 600;
    }
  }

  &.ai-filled-field--reject {
    background: #fef2f2;
    border-left-color: #f44336;

    input {
      border-color: #f44336;
      color: #7f1d1d;
    }
  }

  &.ai-filled-field--review {
    background: #fffbeb;
    border-left-color: #f59e0b;

    input {
      border-color: #f59e0b;
      color: #78350f;
    }
  }
`

const LabelStack = styled.div`
  display: flex;
  flex-direction: column;
  font-size: 12px;
  font-weight: 500;
  color: ${colors.blue1};
  margin-right: 8px;
  min-width: 0;
  overflow: hidden;

  span {
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }
`

const LabelCode = styled.span`
  font-size: 10px;
  font-weight: 400;
  color: ${colors.gray6};
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`

const Label = styled.span`
  font-size: 12px;
  font-weight: 500;
  color: ${colors.blue1};
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  margin-right: 8px;
  min-width: 0;
`

const DataInput = styled.input`
  height: 28px;
  width: 72px;
  flex-shrink: 0;
  border: 1px solid ${colors.gray9};
  border-radius: 4px;
  font-size: 13px;
  color: ${colors.blue3};
  padding: 0 6px;
  text-align: right;
  background: ${colors.white};

  &:focus {
    outline: none;
    border-color: ${colors.blue2};
  }

  &:disabled {
    background: ${colors.gray1};
    color: ${colors.gray6};
  }
`

export default ObjectEditor
